// File: /src/components/Register.js
// Description: Improved registration component with validation, feedback, and streamlined payment handling, including proper Stripe setup

import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';

// Initialize Stripe with your public key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

function Register() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    plan: 'basic', // Default plan
  });
  const { setIsAuthenticated, isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!process.env.REACT_APP_STRIPE_PUBLIC_KEY) {
      console.error('Stripe public key is missing! Please set REACT_APP_STRIPE_PUBLIC_KEY in your .env file.');
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const { email, password, plan } = formData;

  // Handle input change
  const onChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  // Handle registration form submission
  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    console.log(formData);

    try {

      await axios.get(`${process.env.REACT_APP_API_BASE_URL}/auth/logout`, { withCredentials: true });

      // Step 1: Register the user
      const registrationResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/register`,
        // { email, password, plan }
        { email: String(email).trim().toLowerCase(), password, plan }
      );

      // Registration successful, proceed to payment
      if (registrationResponse.status === 201) {
        console.log('Registration successful. Proceeding to payment.');

        // Step 2: Create a checkout session for the selected plan
        const paymentResponse = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/payment/create-checkout-session`,
          { plan },
          { withCredentials: true }
        );

        // Step 3: Redirect to the Stripe checkout page using the public key
        if (paymentResponse.data.id) {
          const stripe = await stripePromise;
          await stripe.redirectToCheckout({ sessionId: paymentResponse.data.id });
        }
      }
    } catch (err) {
      setLoading(false);
      if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <form
        onSubmit={onSubmit}
        className="bg-white p-8 rounded shadow-md w-full max-w-md"
      >
        <h2 className="text-2xl font-bold mb-6 text-center">Register</h2>
        {error && <p className="text-red-500 mb-4">{error}</p>}
        <input
          type="email"
          placeholder="Email"
          name="email"
          value={email}
          onChange={onChange}
          required
          className="w-full p-3 mb-4 border rounded"
        />
        <input
          type="password"
          placeholder="Password"
          name="password"
          value={password}
          onChange={onChange}
          required
          className="w-full p-3 mb-4 border rounded"
        />
        {/* <select
          name="plan"
          value={plan}
          onChange={onChange}
          className="w-full p-3 mb-6 border rounded"
        >
          <option value="basic">Basic</option>
          <option value="standard">Standard</option>
          <option value="premium">Premium</option>
        </select> */}
        <button
          type="submit"
          className="w-full bg-blue-600 text-white p-3 rounded hover:bg-blue-700 transition"
          disabled={loading}
        >
          {loading ? 'Processing...' : 'Register and Pay'}
        </button>
        <div className="mt-4 text-center">
          <Link to="/terms" className="text-blue-600 hover:underline">
            Terms of Service
          </Link>
        </div>
      </form>
    </div>
  );
}

export default Register;
